import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

export const translations = {
  en: {
    language: "Language",
    english: "English",
    swedish: "Swedish",

    home: "Home",
    categories: "Categories",
    advice: "Advice",
    profiles: "Profiles",
    messages: "Messages",
    dashboard: "Dashboard",
    myListings: "My Listings",
    contact: "Contact",
    myProfile: "My Profile",
    admin: "Admin",
    account: "Account",
    logout: "Logout",
    localExchangeCommunity: "Local exchange community",
heroTitle: "Trade and share things, skills, and experiences locally.",
heroText: "Discover useful items, exchange skills, meet people nearby, and join community activities in one trusted place.",
searchThings: "Search things, skills, events…",
enterLocation: "Enter location",
openYourSpace: "Open your space",
shareSomething: "Share something",
whatYouCanExchange: "What you can exchange here",
exchangePoint1: "Browse things, skills, and experiences near you",
exchangePoint2: "Share items and posts with clear previews and feedback",
exchangePoint3: "Connect with people, join events, and message locally",
browseLocalExchanges: "Browse local exchanges",
browseLocalSubtitle: "Explore what your community is sharing, offering, and inviting you to join.",
skills: "Skills",
skillsText: "Find local skills and practical help.",
materials: "Materials",
materialsText: "Browse reusable items near you.",
petsPlants: "Pets & Plants",
petsPlantsText: "Explore care, rescue, and swaps.",
experiences: "Experiences",
experiencesText: "Join nearby hangouts and events.",
adviceText: "Ask the community and get answers.",
localExchangeSimple: "Local exchange, made simple",
localExchangeSimpleText: "Find useful things, trusted people, and nearby activities without the clutter.",
usefulThingsSkills: "Useful things and skills",
usefulThingsSkillsText: "Browse materials, pets, plants, and practical skills shared by your community.",
experiencesTogether: "Experiences that bring people together",
experiencesTogetherText: "Join hangouts, ask for advice, and build local connections through shared experiences.",

    search: "Search",
    searchPlaceholder: "Search materials, pets, hangouts…",

    brandTagline: "Trade and share locally",

    contactPage: {
      title: "Contact Us",
      imageAlt: "Contact illustration",
      name: "Name",
      yourEmail: "Your Email",
      email: "Email",
      subject: "Subject",
      message: "Message",
      namePlaceholder: "Your name",
      subjectPlaceholder: "Subject",
      messagePlaceholder: "Write your message...",
      sendEmail: "Send Email",
      defaultSubject: "New contact message",
      emailAppHint:
        "This will open your email app to send the message to",
      getInTouch: "Get in touch",
      locatedIn: "Located in",
      phone: "Phone",
    },
  },

  sv: {
    language: "Språk",
    english: "Engelska",
    swedish: "Svenska",

    home: "Hem",
    categories: "Kategorier",
    advice: "Råd",
    profiles: "Profiler",
    messages: "Meddelanden",
    dashboard: "Översikt",
    myListings: "Mina annonser",
    contact: "Kontakt",
    myProfile: "Min profil",
    admin: "Admin",
    account: "Konto",
    logout: "Logga ut",
    localExchangeCommunity: "Lokal bytesgemenskap",
heroTitle: "Byt och dela saker, kunskaper och upplevelser lokalt.",
heroText: "Upptäck användbara saker, byt kunskaper, träffa människor nära dig och delta i gemenskapsaktiviteter på en trygg plats.",
searchThings: "Sök saker, kunskaper, evenemang…",
enterLocation: "Ange plats",
openYourSpace: "Öppna din sida",
shareSomething: "Dela något",
whatYouCanExchange: "Vad du kan byta här",
exchangePoint1: "Bläddra bland saker, kunskaper och upplevelser nära dig",
exchangePoint2: "Dela annonser med tydliga förhandsvisningar och feedback",
exchangePoint3: "Kontakta människor, gå på evenemang och skicka meddelanden lokalt",
browseLocalExchanges: "Bläddra bland lokala byten",
browseLocalSubtitle: "Utforska vad din gemenskap delar, erbjuder och bjuder in dig till.",
skills: "Kunskaper",
skillsText: "Hitta lokala kunskaper och praktisk hjälp.",
materials: "Material",
materialsText: "Bläddra bland återanvändbara saker nära dig.",
petsPlants: "Husdjur & växter",
petsPlantsText: "Utforska omsorg, omplacering och byten.",
experiences: "Upplevelser",
experiencesText: "Delta i närliggande aktiviteter och evenemang.",
adviceText: "Fråga gemenskapen och få svar.",
localExchangeSimple: "Lokala byten, enkelt gjort",
localExchangeSimpleText: "Hitta användbara saker, pålitliga personer och aktiviteter nära dig utan krångel.",
usefulThingsSkills: "Användbara saker och kunskaper",
usefulThingsSkillsText: "Bläddra bland material, husdjur, växter och praktiska kunskaper som delas av din gemenskap.",
experiencesTogether: "Upplevelser som för människor samman",
experiencesTogetherText: "Delta i aktiviteter, fråga om råd och bygg lokala kontakter genom delade upplevelser.",

    search: "Sök",
    searchPlaceholder: "Sök material, husdjur, aktiviteter…",

    brandTagline: "Byt och dela lokalt",

    contactPage: {
      title: "Kontakta oss",
      imageAlt: "Kontaktillustration",
      name: "Namn",
      yourEmail: "Din e-post",
      email: "E-post",
      subject: "Ämne",
      message: "Meddelande",
      namePlaceholder: "Ditt namn",
      subjectPlaceholder: "Ämne",
      messagePlaceholder: "Skriv ditt meddelande...",
      sendEmail: "Skicka e-post",
      defaultSubject: "Nytt kontaktmeddelande",
      emailAppHint:
        "Detta öppnar din e-postapp för att skicka meddelandet till",
      getInTouch: "Kontakta oss",
      locatedIn: "Finns i",
      phone: "Telefon",
    },
  },
};

const LanguageContext = createContext({
  language: "en",
  setLanguage: () => {},
  t: (key) => key,
});

export function LanguageProvider({ children }) {
  const [language, setLanguageState] = useState(() => {
    const saved = localStorage.getItem("bytamed-language");

    if (saved === "sv" || saved === "en") {
      return saved;
    }

    return navigator.language?.toLowerCase().startsWith("sv")
      ? "sv"
      : "en";
  });

  useEffect(() => {
    localStorage.setItem("bytamed-language", language);
    document.documentElement.lang = language;
  }, [language]);

  const value = useMemo(() => {
    const getNestedValue = (obj, path) => {
      return path
        .split(".")
        .reduce((acc, part) => acc?.[part], obj);
    };

    return {
      language,

      setLanguage: (next) => {
        setLanguageState(next === "sv" ? "sv" : "en");
      },

      t: (key) => {
        return (
          getNestedValue(translations[language], key) ||
          getNestedValue(translations.en, key) ||
          key
        );
      },
    };
  }, [language]);

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  return useContext(LanguageContext);
}